import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  CalendarCheck,
  CalendarPlus,
  Wallet,
  ArrowRight,
  Clock,
  MapPin,
  Sparkles,
  User,
  Hash,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { Reveal } from './site/motion';
import { discoveryCategories } from './site/navConfig';
import { ReminderPreferences } from './ReminderPreferences';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  confirmed: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  pending: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  completed: 'bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300',
  cancelled: 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300',
};

const AuthenticatedHome = () => {
  const { user: legacyUser } = useAuth();
  const { user: unifiedUser, sessionToken, userRole } = useUnifiedAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const user = unifiedUser || legacyUser;
  const role = (userRole || user?.user_type || user?.role || 'patient').toLowerCase();

  const [appointments, setAppointments] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = sessionToken || localStorage.getItem('sessionToken');

  const loadDashboard = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    const [apptRes, walletRes] = await Promise.allSettled([
      axios.get(`${API}/appointments`, { headers }),
      axios.get(`${API}/wallet/balance`, { headers }),
    ]);

    if (apptRes.status === 'fulfilled') {
      const list = Array.isArray(apptRes.value.data) ? apptRes.value.data : apptRes.value.data?.appointments || [];
      setAppointments(list);
    }
    if (walletRes.status === 'fulfilled') {
      setWalletBalance(Number(walletRes.value.data?.balance || 0));
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (role === 'provider') {
      navigate('/provider-dashboard');
      return;
    }
    if (role === 'doctor') {
      navigate('/clinic-dashboard');
      return;
    }
    loadDashboard();
  }, [role, navigate, loadDashboard]);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('dashboardHome.morning', 'Good morning');
    if (hour < 17) return t('dashboardHome.afternoon', 'Good afternoon');
    return t('dashboardHome.evening', 'Good evening');
  };

  const isUpcoming = (appt) => {
    if (['cancelled', 'completed'].includes((appt.status || '').toLowerCase())) return false;
    if (!appt.appointment_date) return false;
    const when = new Date(`${appt.appointment_date}T${appt.appointment_time || '00:00'}`);
    return when.getTime() >= Date.now() - 60 * 60 * 1000;
  };

  const upcoming = appointments.filter(isUpcoming).sort((a, b) =>
    `${a.appointment_date}${a.appointment_time}`.localeCompare(`${b.appointment_date}${b.appointment_time}`)
  );

  const stats = [
    {
      id: 'upcoming',
      icon: CalendarCheck,
      value: upcoming.length,
      label: t('dashboardHome.upcoming', 'Upcoming appointments'),
      chip: 'bg-blue-50 dark:bg-blue-500/10',
      accent: 'text-blue-600 dark:text-blue-400',
    },
    {
      id: 'total',
      icon: Hash,
      value: appointments.length,
      label: t('dashboardHome.totalBookings', 'Total bookings'),
      chip: 'bg-teal-50 dark:bg-teal-500/10',
      accent: 'text-teal-600 dark:text-teal-400',
    },
  ];

  return (
    <div
      data-testid="customer-dashboard"
      className="relative min-h-screen overflow-hidden bg-gray-50 dark:bg-gray-950 transition-colors duration-300"
    >
      <span className="aurora-blob w-[30rem] h-[30rem] -top-44 start-[-8rem] bg-blue-300/45 dark:bg-blue-600/25 animate-aurora" />
      <span className="aurora-blob w-[22rem] h-[22rem] top-10 end-[-6rem] bg-emerald-300/40 dark:bg-emerald-500/20 animate-aurora-slow" />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-10">
        {/* Greeting */}
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="text-start">
            <span className="inline-flex items-center gap-2 rounded-full glass-chip px-3.5 py-1.5">
              <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
              <span className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-200">
                {greeting()}
              </span>
            </span>
            <h1 className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              {user?.name || t('dashboardHome.there', 'there')}
            </h1>
            <p className="mt-3 text-base text-gray-600 dark:text-gray-400">
              {t('dashboardHome.subtitle', 'Your appointments, wallet and favourite places — all in one view.')}
            </p>
          </div>
          <button
            onClick={() => navigate('/healthcare-providers')}
            data-testid="dashboard-book-cta"
            className="btn-shine group inline-flex items-center justify-center gap-2 self-start rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-blue-600/25 lift"
          >
            <CalendarPlus className="w-4 h-4" />
            {t('dashboardHome.bookNew', 'Book a new appointment')}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
          </button>
        </Reveal>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map((stat, i) => {
            const Icon = stat.icon;
            return (
              <Reveal key={stat.id} delay={i * 90}>
                <div
                  data-testid={`dashboard-stat-${stat.id}`}
                  className="h-full rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 lift hover:shadow-xl"
                >
                  <span className={`grid place-items-center w-11 h-11 rounded-2xl ${stat.chip}`}>
                    <Icon className={`w-5 h-5 ${stat.accent}`} />
                  </span>
                  <p className="font-display mt-5 text-3xl font-extrabold text-gray-900 dark:text-white numeric-ltr">
                    {loading ? (
                      <span className="block h-8 w-16 rounded-lg bg-gray-200 dark:bg-white/10 animate-pulse" />
                    ) : (
                      stat.value
                    )}
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
                </div>
              </Reveal>
            );
          })}

          <Reveal delay={180}>
            <button
              onClick={() => navigate('/wallet')}
              data-testid="dashboard-wallet-card"
              className="relative h-full w-full text-start overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-600 to-emerald-500 p-6 lift hover:shadow-2xl grain"
            >
              <span className="aurora-blob w-40 h-40 -top-14 end-[-2rem] bg-white/30 animate-aurora" />
              <span className="relative grid place-items-center w-11 h-11 rounded-2xl bg-white/15 border border-white/20">
                <Wallet className="w-5 h-5 text-white" />
              </span>
              <p className="relative font-display mt-5 text-3xl font-extrabold text-white numeric-ltr">
                {walletBalance === null ? (
                  <span className="block h-8 w-32 rounded-lg bg-white/20 animate-pulse" />
                ) : (
                  `${walletBalance.toFixed(3)} KD`
                )}
              </p>
              <p className="relative mt-1 text-sm text-white/75">
                {t('dashboardHome.walletBalance', 'Wallet balance · tap to top up')}
              </p>
            </button>
          </Reveal>
        </div>

        {/* Upcoming appointments */}
        <section className="space-y-4">
          <Reveal className="flex items-center justify-between">
            <h2 className="font-display text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {t('dashboardHome.yourAppointments', 'Your upcoming appointments')}
            </h2>
            <button
              onClick={() => navigate('/appointments')}
              data-testid="dashboard-view-all-appointments"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 dark:text-blue-400"
            >
              {t('dashboardHome.viewAll', 'View all')}
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
            </button>
          </Reveal>

          {loading ? (
            <div className="space-y-3">
              {[0, 1].map((i) => (
                <div
                  key={i}
                  className="h-24 rounded-3xl border border-gray-200 dark:border-white/10 bg-white dark:bg-gray-900/60 animate-pulse"
                />
              ))}
            </div>
          ) : upcoming.length > 0 ? (
            <div className="space-y-3">
              {upcoming.slice(0, 4).map((appt, i) => {
                const status = (appt.status || 'pending').toLowerCase();
                return (
                  <Reveal key={appt.id} delay={i * 80}>
                    <button
                      onClick={() => navigate('/appointments')}
                      data-testid="dashboard-appointment-card"
                      className="group w-full text-start rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-5 lift hover:shadow-xl hover:border-blue-300/70 dark:hover:border-blue-400/30"
                    >
                      <div className="flex flex-wrap items-start gap-4">
                        <span className="grid place-items-center w-12 h-12 shrink-0 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 text-white font-bold">
                          {(appt.doctor_name || appt.clinic_name || '?')[0]}
                        </span>
                        <div className="min-w-0 flex-1">
                          <h3 className="font-display font-bold text-gray-900 dark:text-white truncate">
                            {appt.doctor_name || appt.clinic_name || t('dashboardHome.provider', 'Provider')}
                          </h3>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                            {appt.service_name || t('dashboardHome.appointment', 'Appointment')}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarCheck className="w-3.5 h-3.5 text-blue-500" />
                              <span className="numeric-ltr">{appt.appointment_date}</span>
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Clock className="w-3.5 h-3.5 text-emerald-500" />
                              <span className="numeric-ltr">{appt.appointment_time}</span>
                            </span>
                            {appt.reference_number && (
                              <span className="inline-flex items-center gap-1.5 font-mono numeric-ltr">
                                <Hash className="w-3.5 h-3.5 text-gray-400" />
                                {appt.reference_number}
                              </span>
                            )}
                          </div>
                        </div>
                        <span
                          className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${
                            STATUS_STYLES[status] || STATUS_STYLES.pending
                          }`}
                        >
                          {status}
                        </span>
                      </div>
                    </button>
                  </Reveal>
                );
              })}
            </div>
          ) : (
            <Reveal>
              <div
                data-testid="dashboard-appointments-empty"
                className="rounded-3xl border border-dashed border-gray-300 dark:border-white/15 bg-white/60 dark:bg-white/[0.02] p-10 text-center"
              >
                <span className="mx-auto grid place-items-center w-14 h-14 rounded-3xl bg-gray-100 dark:bg-white/5">
                  <CalendarPlus className="w-6 h-6 text-gray-400" />
                </span>
                <h3 className="font-display mt-5 text-lg font-bold text-gray-900 dark:text-white">
                  {t('dashboardHome.emptyTitle', 'No upcoming appointments')}
                </h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  {t('dashboardHome.emptyDesc', 'Browse verified clinics, salons and spas and book in a few taps.')}
                </p>
                <button
                  onClick={() => navigate('/healthcare-providers')}
                  data-testid="dashboard-empty-browse-btn"
                  className="mt-6 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-blue-600 to-emerald-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 lift"
                >
                  {t('landing.exploreAll', 'Explore all providers')}
                </button>
              </div>
            </Reveal>
          )}
        </section>

        {/* Quick access */}
        <section className="space-y-4">
          <Reveal>
            <h2 className="font-display text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {t('dashboardHome.quickAccess', 'Book by category')}
            </h2>
          </Reveal>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {discoveryCategories.map((cat, i) => {
              const Icon = cat.icon;
              return (
                <Reveal key={cat.id} delay={i * 60}>
                  <button
                    onClick={() => navigate(cat.to)}
                    data-testid={`dashboard-category-${cat.id}`}
                    className="w-full h-full rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-5 text-center lift hover:shadow-xl hover:border-blue-300/70 dark:hover:border-blue-400/30"
                  >
                    <span className={`mx-auto grid place-items-center w-11 h-11 rounded-2xl ${cat.chip}`}>
                      <Icon className={`w-5 h-5 ${cat.accent}`} />
                    </span>
                    <span className="mt-3 block text-xs font-semibold text-gray-800 dark:text-gray-200">
                      {t(cat.labelKey, cat.fallback)}
                    </span>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </section>

        {/* Reminders */}
        <Reveal>
          <ReminderPreferences token={token} />
        </Reveal>

        {/* Profile shortcut */}
        <Reveal>
          <button
            onClick={() => navigate('/profile')}
            data-testid="dashboard-profile-shortcut"
            className="w-full flex items-center gap-4 rounded-3xl border border-gray-200/80 dark:border-white/10 bg-white dark:bg-gray-900/60 p-6 text-start lift hover:shadow-xl"
          >
            <span className="grid place-items-center w-11 h-11 rounded-2xl bg-violet-50 dark:bg-violet-500/10">
              <User className="w-5 h-5 text-violet-600 dark:text-violet-400" />
            </span>
            <span className="flex-1">
              <span className="block font-display font-bold text-gray-900 dark:text-white">
                {t('nav.myProfile', 'My Profile')}
              </span>
              <span className="block text-sm text-gray-500 dark:text-gray-400">
                {t('dashboardHome.profileDesc', 'Keep your contact details and preferences up to date')}
              </span>
            </span>
            <MapPin className="hidden sm:block w-4 h-4 text-gray-300" />
            <ArrowRight className="w-4 h-4 text-gray-400 rtl:rotate-180" />
          </button>
        </Reveal>
      </div>
    </div>
  );
};

export default AuthenticatedHome;
